import { apiCall, apiCallWithCsrfRefresh } from "./api";
import { getAnonPreferencesDraft } from "./anonDraft";
import type { CarbCoverage } from "@shared/modules/fuelPlans/carbCoverage";

/** A selectable option in a preference card group. Mirrors `PreferenceOption`. */
export type PreferenceOption = {
  key: string;
  label: string;
  hint?: string;
};

export type PreferenceOptionCatalog = {
  dietaryNeeds: PreferenceOption[];
  dietaryPreferences: PreferenceOption[];
  fuelTypes: PreferenceOption[];
  flavorProfiles: PreferenceOption[];
  brands: PreferenceOption[];
};

/** Server `SerializedPreferences` shape (preferences/service.ts). */
export type SerializedPreferences = {
  customerId: string;
  /**
   * True when a `CustomerPreferences` row exists server-side. The GET returns a
   * full object (empty lists + caffeine defaults) even when no row exists, so
   * this is the only way to tell "never saved" from "saved empty". Optional for
   * back-compat with servers predating the field — treat absent as unknown and
   * fall back to gap-fill semantics.
   */
  exists?: boolean;
  dietaryNeeds: string[];
  dietaryPreferences: string[];
  preferredProductTypes: string[];
  excludedProductTypes: string[];
  preferredFlavorProfiles: string[];
  excludedFlavorProfiles: string[];
  preferredBrands: string[];
  excludedBrands: string[];
  caffeineAllowed: boolean;
  caffeineMaxMgPerHour: number | null;
  caffeineMaxPercentOfFuel: number;
  /** SPA display-unit preference (render-only; stored data stays metric). */
  distanceUnit: "km" | "mi";
  /** Seconds per mile; null when unset. */
  averageRunningPaceSecondsPerMile?: number | null;
  minFieldsComplete: boolean;
  lastSyncedToShopifyAt: string | null;
  lastSyncedFromShopifyAt: string | null;
};

export type PreferencesResponse = {
  preferences: SerializedPreferences | null;
  options: PreferenceOptionCatalog;
};

/** Payload the v3 save endpoint accepts — paired prefer/exclude lists. */
export type SavePreferencesInput = {
  dietaryNeeds: string[];
  dietaryPreferences: string[];
  preferredProductTypes: string[];
  excludedProductTypes: string[];
  preferredFlavorProfiles: string[];
  excludedFlavorProfiles: string[];
  preferredBrands: string[];
  excludedBrands: string[];
  caffeineAllowed: boolean;
  caffeineMaxMgPerHour: number | null;
  caffeineMaxPercentOfFuel: number;
};

/**
 * The anon browser-draft preferences for a generate body (Slice C,
 * preferences-outbound-alignment): guests have no server prefs row, so their
 * draft rides the generate POST and becomes the request's frozen preferences
 * snapshot server-side. Undefined when no draft exists. Safe on every generate
 * call: signed-in customers' drafts are cleared by the login promotion, and
 * the server ignores the field for customer principals regardless (the DB row
 * is authoritative). Drafts may be PARTIAL (about-you writes dietary+flavours
 * only) — the server schema defaults omitted lists.
 */
export function anonDraftPreferencesForGenerate(): Partial<SavePreferencesInput> | undefined {
  return getAnonPreferencesDraft<Partial<SavePreferencesInput>>() ?? undefined;
}

const BASE = "/api/preferences";

export async function loadPreferences(signal?: AbortSignal): Promise<PreferencesResponse> {
  return apiCall<PreferencesResponse>(`${BASE}/me`, { signal });
}

export async function savePreferences(
  input: SavePreferencesInput,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<PreferencesResponse> {
  return apiCallWithCsrfRefresh<PreferencesResponse>(
    `${BASE}/me`,
    { method: "POST", body: input, csrfToken },
    refreshCsrf
  );
}

/**
 * Persist the display-unit preference (miles/km) for a signed-in customer.
 * Separate from {@link savePreferences} — this endpoint skips the Shopify sync,
 * so the units toggle can fire freely without burning metafield-write quota.
 */
export async function saveDistanceUnit(
  unit: "km" | "mi",
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<PreferencesResponse> {
  return apiCallWithCsrfRefresh<PreferencesResponse>(
    `${BASE}/me/units`,
    { method: "POST", body: { distanceUnit: unit }, csrfToken },
    refreshCsrf
  );
}

/**
 * Persist average running pace (seconds per mile). Separate from fueling
 * preferences — no Shopify sync. Guests use browser storage instead.
 */
export async function saveRunningPace(
  averageRunningPaceSecondsPerMile: number,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<PreferencesResponse> {
  return apiCallWithCsrfRefresh<PreferencesResponse>(
    `${BASE}/me/running-pace`,
    { method: "POST", body: { averageRunningPaceSecondsPerMile }, csrfToken },
    refreshCsrf
  );
}

// ─── Plans history ──────────────────────────────────────────────────────────

/**
 * Plans-history row. Mirrors `TimelineRow` from `src/modules/fuelPlans/heroMerge.ts`
 * — a discriminated union so an in-flight regenerate appears as a `Generating`
 * row that links to `/plan/pending/:requestId`, deduplicated with the completed
 * plan that supersedes it.
 */
export type FuelPlanRequestRow = {
  kind: "request";
  requestId: string;
  // Future server-side statuses survive the type check via the `(string & {})`
  // escape — see `lib/dashboardApi.ts` for the rationale.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  status: "processing" | "requested" | "failed" | (string & {});
  createdAt: string;
  periodStart: string | null;
  periodEnd: string | null;
  /**
   * Curated customer-facing copy when the request failed. Always one of a
   * small closed set of strings — never raw upstream text. Null while the
   * request is in flight or completed.
   */
  failureSummary: string | null;
  /** v4 journey wiring (mirrors HeroRequestItem): lets a timeline rehydrate an
   *  in-flight box/kit build after a reload. Null for standalone requests;
   *  optional until the server sends them. */
  journeyId?: string | null;
  dropIndex?: number | null;
  /**
   * The LOGICAL box index this build targets, resolved at generate time
   * (§5.4). `dropIndex` is only the rail-SLOT pointer, so a regenerate whose
   * source is a paid (slot-less) sibling carries `dropIndex: null` while still
   * being a box build — classifying on `dropIndex` alone rendered it as a
   * race-day preview spinner. Prefer this field; fall back to `dropIndex`.
   * Optional until the server projection ships it.
   */
  targetDropIndex?: number | null;
  previewEventId?: string | null;
};

export type FuelPlanPlanRow = {
  kind: "plan";
  id: string;
  status: string;
  coveragePercent: number | null;
  /** During-only carb coverage snapshot — drives the history card g/wk stat. */
  carbCoverage?: CarbCoverage | null;
  /** Planner period in weeks (frozen `FuelPlanRequest.periodCount`). */
  periodCount?: number;
  periodStart: string | null;
  periodEnd: string | null;
  /**
   * Inclusive fueled calendar window (anchor + offset + workout-span weeks × 7).
   * Prefer for history-table "Period" labels; `periodStart`/`periodEnd` remain
   * first/last workout snapshot dates.
   */
  fuelPeriodStart: string | null;
  fuelPeriodEnd: string | null;
  periodTotals: {
    carbsGrams: number;
    sodiumMg: number;
    caffeineMg: number;
    units: number;
    priceCents: number;
    /** False when a counted line has no published catalog price, so
     *  `priceCents` is a partial sum (F-12-5). Optional/additive — a missing
     *  value means "complete". */
    priceComplete?: boolean;
  } | null;
  /** Unique SKUs in the box — not `periodTotals.units` (containers). */
  productCount?: number;
  purchasedAt: string | null;
  generatedAt: string | null;
  requestId: string;
  createdAt: string;
  /** Number of paid orders against this plan (incl. reorders). */
  orderCount?: number;
  /** v4 journey wiring (mirrors server HeroPlanItem). */
  planKind?: "standalone" | "drop" | "race_day_preview";
  journeyId?: string | null;
  dropIndex?: number | null;
  /**
   * The plan's window ANCHOR (UTC-midnight ISO). `dropIndex` is only the rail
   * SLOT pointer, so a paid or superseded sibling carries `dropIndex: null`
   * while still belonging to a numbered box — match this against the journey's
   * window `startYmd`s to recover the logical number (`logicalBoxIndexFor`).
   * Optional until the server projection ships it.
   */
  planAnchoredAt?: string | null;
  /**
   * Regeneration lineage (mirrors server `HeroPlanItem`). `sourcePlanId` is the
   * plan this one was regenerated FROM — null means UNKNOWN ancestry (a fresh
   * plan, or one generated before lineage was recorded), never proof it wasn't
   * a regenerate.
   */
  sourcePlanId?: string | null;
  /**
   * The newest still-usable plan regenerated from this one, chosen server-side
   * (lineage branches; there is no single authoritative successor). Supersession
   * is NOT expiry — a source with a successor stays viewable and purchasable
   * until its own TTL — so this only ever means "a newer version exists".
   * Undefined outside the plans timeline.
   */
  supersededByPlanId?: string | null;
  rampWeekLabel?: string | null;
};

export type FuelPlanSummary = FuelPlanRequestRow | FuelPlanPlanRow;

export async function loadPlanHistory(signal?: AbortSignal): Promise<FuelPlanSummary[]> {
  const res = await apiCall<{ plans: FuelPlanSummary[] }>("/api/fuel-plans/plans", { signal });
  return res.plans;
}
